import { html } from 'lit-element';
import {
  defaultResponseFontSize,
  fontFamily,
  responseStyles,
} from '../cssUtils.js';
import { CustomLitElement } from '../CustomLitElement.js';

/**
 * @element drawing-response
 * @prop {Object} model - The model for the pie
 * @prop {Object} session - The response session
 * @cssprop --pie-live-session-background-color - change the background color. Should be used only if there's no imageUrl
 * @cssprop --pie-live-session-font-family - change the font family
 * @cssprop --pie-live-session-font-size-small - change the font size
 * @cssprop --pie-live-session-response-height - change the maximum height
 * @cssprop --pie-live-session-response-width - change the maximum width
 */
export class DrawingResponse extends CustomLitElement {
  static get properties() {
    return {
      model: { type: Object, attribute: false },
      session: { type: Array, attribute: false },
      containerDimensions: { type: Object },
    };
  }

  createRenderRoot() {
    return this;
  }

  set containerDimensions(val) {
    const oldVal = this._containerDimensions;

    if (
      (!oldVal && val) ||
      (oldVal &&
        val &&
        (oldVal.width !== val.width || oldVal.height !== val.height))
    ) {
      this._containerDimensions = val;
      this.requestUpdate('containerDimensions', oldVal);
    }
  }

  get containerDimensions() {
    return this._containerDimensions;
  }

  updated(changedProps) {
    super.updated(changedProps);

    const { height, width } = this._containerDimensions || {};

    this.updateComplete.then(() => {
      setTimeout(() => {
        const { clientHeight, clientWidth } = this.querySelector('.response');

        if (
          clientHeight &&
          clientWidth &&
          (height !== clientHeight || width !== clientWidth)
        ) {
          this.containerDimensions = {
            width: clientWidth,
            height: clientHeight,
          };
        }
      }, 500);
    });
  }

  _getMinWidthAndHeight() {
    // find out the minimum height and width that is needed in the initial scene
    // to make sure every item fits
    const { imageDimensions } = this.model || {};
    const { drawables = [], texts = [] } = this.session || {};
    // if there's an image, the initial width and height will be from image dimensions
    let { height: initialHeight, width: initialWidth } = imageDimensions || {};

    // make sure every text item fits
    texts.forEach(({ x, y, width, fontSize }) => {
      if (x + width > initialWidth) {
        initialWidth = x + width;
      }

      if (y + fontSize > initialHeight) {
        initialHeight = y + fontSize;
      }
    });

    // make sure every drawable item fits
    return drawables.reduce(
      (acc, drawing) => {
        const { type, startx, starty, x, y, height, points, radius, width } =
          drawing || {};
        let maxX;
        let maxY;

        switch (type) {
          case 'FreePathDrawable':
          case 'EraserDrawable': {
            maxX = Math.max(...points.filter((p, index) => index % 2 === 0));
            maxY = Math.max(...points.filter((p, index) => index % 2 === 1));
            break;
          }
          case 'LineDrawable': {
            maxX = Math.max(startx, x);
            maxY = Math.max(starty, y);
            break;
          }
          case 'RectangleDrawable': {
            maxX = Math.max(startx, startx + width);
            maxY = Math.max(starty, starty + height);

            break;
          }
          case 'CircleDrawable': {
            maxX = Math.max(startx, startx + radius);
            maxY = Math.max(starty, starty + radius);
            break;
          }
          default:
            break;
        }

        if (maxX > acc.width) {
          acc.width = maxX;
        }

        if (maxY > acc.height) {
          acc.height = maxY;
        }

        return acc;
      },
      {
        width: initialWidth,
        height: initialHeight,
      }
    );
  }

  static _renderDrawing(drawing, maxX, maxY) {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');

    c.width = maxX;
    c.height = maxY;
    c.style.position = 'absolute';
    c.style.top = '0';
    c.style.left = '0';

    const { type, fillColor, outlineColor } = drawing;

    ctx.fillStyle = fillColor;
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = 2;

    ctx.beginPath();

    switch (type) {
      case 'FreePathDrawable':
      case 'EraserDrawable': {
        const { points } = drawing;

        if (type === 'EraserDrawable') {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 5;
        }

        for (let i = 0; i < points.length; i += 1) {
          if (i % 2 !== 0) {
            ctx.lineTo(points[i + 1], points[i]);
          }
        }

        break;
      }
      case 'LineDrawable': {
        const { startx, starty, x, y } = drawing;

        ctx.moveTo(startx, starty);
        ctx.lineTo(x, y);

        break;
      }
      case 'RectangleDrawable': {
        const { startx, starty, width, height } = drawing;

        ctx.fillRect(startx, starty, width, height);
        ctx.strokeRect(startx, starty, width, height);

        break;
      }
      case 'CircleDrawable': {
        const { radius, startx, starty } = drawing;

        ctx.arc(startx, starty, radius, 0, 2 * Math.PI);
        ctx.fill();

        break;
      }
      default:
        break;
    }

    ctx.stroke();

    return html`${c}`;
  }

  static _renderText({ text, x, y, rotation, scaleX, scaleY }) {
    const style = `top: ${
      y - 3
    }px; left: ${x}px; transform: rotate(${rotation}deg) scaleX(${scaleX}) scaleY(${scaleY});`;

    return html`<span class="drawableText" style="${style}">${text}</span>`;
  }

  _renderImage() {
    const { imageUrl, imageDimensions } = this.model || {};
    const { height, width } = imageDimensions || {};

    if (!imageUrl) return html``;

    return html`<img
      alt="Image"
      src="${imageUrl}"
      height="${height}px"
      width="${width}px"
    />`;
  }

  _renderContent({ height, width }) {
    const { drawables, texts } = this.session || {};
    const itemsStyle = `width: ${width}px; height: ${height}px;`;
    const drawablesItems =
      drawables &&
      drawables.map(drawable =>
        DrawingResponse._renderDrawing(drawable, width, height)
      );

    return html` <div class="scalable">
      ${this._renderImage()}

      <div class="drawables" style=${itemsStyle}>
        ${drawablesItems} ${texts && texts.map(DrawingResponse._renderText)}
      </div>
    </div>`;
  }

  render() {
    const { height, width } = this._getMinWidthAndHeight();
    const { height: cHeight, width: cWidth } = this._containerDimensions || {};

    // the drawables and texts are defined by points like x, y, width, height, etc - these values are related to initial scene width and height
    // instead of recalculating each value in order to fit in the max-height and max-width container
    // we just scale the entire scene to fit
    return html` <style>
        ${responseStyles}
        .drawing.response {
          position: relative;
          padding: 0;
        }

        .drawing .scalable {
          transform-origin: top left;
        }

        .drawing .drawables {
          position: absolute;
          left: 0;
          top: 0;
        }

        .drawing .drawableText {
          ${fontFamily}
          font-size: var(--pie-live-session-font-size-small, ${defaultResponseFontSize}px);
          position: absolute;
          transform-origin: left;
        }

        .drawing .scalable {
          transform: scale(min(${cHeight / height}, ${cWidth / width}, 1));
        }
      </style>

      <div class="response drawing">
        ${this._containerDimensions && this._renderContent({ height, width })}
      </div>`;
  }
}

export default DrawingResponse;
